import type { GameData } from './data.js';
import { calculateDps, type DpsInput, type DpsOutput } from '@divcalc/formulas';
import { findGearTalentBonus } from './data/gear-talent-bonuses.js';
import { findMod, MOD_SLOTS, type ModSlot } from './data/weapon-mods.js';
import { findSpec } from './data/specializations.js';
import { findSet4pc } from './data/set-4pc-bonuses.js';
import { findGearMod } from './data/gear-mods.js';
import { resolveSlot3 } from './data/weapon-attrs.js';
import { applyHeadhunterCap } from '@divcalc/formulas';

export type SlotKey = 'chest' | 'backpack' | 'gloves' | 'mask' | 'holster' | 'kneepads';

export type AttrStat =
  | 'chc'
  | 'chd'
  | 'hsd'
  | 'dta'
  | 'dth'
  | 'ooc'
  | 'wd'
  | 'mag'
  | 'reload'
  | 'handling'
  | 'status_effects'
  | 'burn_duration'
  | 'bleed_duration'
  | 'poison_duration';

// Gear mod is a separate value from attrs — stores the full mod id
export type GearModId = string;

export interface GearSlot {
  brandId: string | null;
  setId: string | null;
  /** If set, this slot holds a Named gear item (mutually exclusive with brand/set) */
  namedId: string | null;
  coreStat: 'wd' | 'armor' | 'skill_tier';
  attr1: AttrStat | null;
  attr2: AttrStat | null;
  modAttr: GearModId | null;
  /** Prototype gear item — uses non-linear skill_tier scaling (prototype-skill-tier.json)
   *  and may carry a Prototype Augment talent (prototype-augments.json). */
  isPrototype: boolean;
  /** Prototype Augment id (from prototype-augments.json). Only meaningful when isPrototype=true. */
  prototypeAugmentId: string | null;
}

export const SLOT_KEYS: SlotKey[] = ['chest', 'backpack', 'gloves', 'mask', 'holster', 'kneepads'];

export const MOD_SLOT_KEYS: Set<SlotKey> = new Set(['chest', 'backpack', 'mask']);

// Max expertise grade (TU22+ Elevated). Y8S1 pre-TU22 was 30; TU22 added G31 Elevated tier.
export const EXPERTISE_MAX_GRADE = 31;
export const EXPERTISE_ELEVATED_GRADE = 31;

// Maps internal slot key to expertise.json bucket name.
const EXPERTISE_SLOT_BUCKET: Record<SlotKey, string> = {
  chest: 'gear_chest',
  backpack: 'gear_back',
  gloves: 'gear_gloves',
  mask: 'gear_mask',
  holster: 'gear_holster',
  kneepads: 'gear_kneepads',
};

export type ExpertiseStat = 'CoreRollBonus' | 'AttributeRollBonus' | 'WeaponDamage' | 'SkillPowerBonus';

/**
 * Lookup per-slot / per-grade expertise bonus from expertise.json (fetched into /data/expertise.json).
 *
 * Replaces the old hardcoded `armorPct = grade * 0.3` heuristic — raw data has explicit
 * CoreRollBonus + AttributeRollBonus per gear bucket and per grade (0..30 + 31_elevated).
 *
 * @param expertiseJson  Parsed contents of /data/expertise.json (pass the cached object — caller fetches once)
 * @param slot           Internal slot key ('chest' | 'backpack' | ...). Mapped to gear_back/gear_chest/etc.
 * @param grade          0..31. 31 returned as `level_31_elevated`.
 * @param statType       Which field to read. Defaults to CoreRollBonus.
 * @returns              The bonus as a decimal (0.24 = 24%). 0 if missing.
 */
export function getExpertiseBonus(
  expertiseJson: Record<string, Record<string, Record<string, number | string>>> | null | undefined,
  slot: SlotKey,
  grade: number,
  statType: ExpertiseStat = 'CoreRollBonus',
): number {
  if (!expertiseJson) return 0;
  const bucket = EXPERTISE_SLOT_BUCKET[slot];
  const bucketData = expertiseJson[bucket];
  if (!bucketData) return 0;
  const clamped = Math.max(0, Math.min(EXPERTISE_MAX_GRADE, Math.floor(grade)));
  const levelKey = clamped === EXPERTISE_ELEVATED_GRADE ? 'level_31_elevated' : `level_${clamped}`;
  const level = bucketData[levelKey];
  if (!level) return 0;
  const v = level[statType];
  return typeof v === 'number' ? v : 0;
}

// Default attr rolls for REGULAR (non-prototype) gear items — TU22.1 verified via user screenshots.
// Prototype items have higher caps (15/25/25) but most players use regular gear.
const ATTR_DEFAULTS: Record<AttrStat, number> = {
  chc: 6,
  chd: 12,
  hsd: 12,
  wd: 8,
  dta: 12,
  dth: 12,
  status_effects: 0,
  burn_duration: 0,
  bleed_duration: 0,
  poison_duration: 0,
  ooc: 12,
  handling: 8,
  mag: 21,
  reload: 10,
};

function emptySlot(): GearSlot {
  return { brandId: null, setId: null, namedId: null, coreStat: 'wd', attr1: null, attr2: null, modAttr: null, isPrototype: false, prototypeAugmentId: null };
}

export function createBuildState() {
  let weaponId: string | null = $state(null);
  let weaponTalentActive = $state(false);
  let weaponTalentStacks = $state(0);
  let customWeaponTalentId: string | null = $state(null);
  /** User pick for the 3rd weapon attribute slot. null → use weapon's intrinsicAttrs[2] default. */
  let weaponAttrSelected: string | null = $state(null);
  let gear: Record<SlotKey, GearSlot> = $state({
    chest: emptySlot(),
    backpack: emptySlot(),
    gloves: emptySlot(),
    mask: emptySlot(),
    holster: emptySlot(),
    kneepads: emptySlot(),
  });
  let chestTalentId: string | null = $state(null);
  let chestTalentActive = $state(false);
  let backpackTalentId: string | null = $state(null);
  let backpackTalentActive = $state(false);
  let shdWatchActive = $state(true);
  let weaponMods: Record<ModSlot, string | null> = $state({
    optic: null,
    muzzle: null,
    underbarrel: null,
    magazine: null,
  });
  let specializationId: string | null = $state(null);
  /** Weapon classes player invested talent points into (+15% WD each, max 3). */
  let specClassPicks: string[] = $state([]);
  /** Unlocked MMR/Rifle HSD tier (+45% HSD to MMR & Rifle) — only for Sharpshooter/Survivalist. */
  let specMmrRifleHsd: boolean = $state(false);
  /** Active spec tree perks (by id — see SPECS[].treePerks). */
  let activeSpecPerks: string[] = $state([]);
  let groupSize = $state(1);
  let targetStatus: 'none' | 'burning' | 'bleeding' | 'shocked' | 'poisoned' | 'blinded' = $state('none');
  let targetPulsed = $state(false);
  /** Whether the target carries a Negotiator's Dilemma mark (or similar mark mechanic). */
  let targetMarked = $state(false);
  /** Single-shot proc mode: when true, calc shows PEAK on the next-shot (Dodge City / Centurion).
   * When false (default) — bonus averaged across magazine for sustained DPS realism. */
  let firstShotMode = $state(false);
  /** Number of Perfect-variant gear talents equipped (0-3) — drives Harrier Pride backpack scaling. */
  let harrierPerfectCount = $state(3);
  /** Ravenous (Geri & Freki) proc active — controls whether the +500% wd burst is currently up.
   *  Default true (calc shows peak), user can disable for sustained DPS without proc. */
  let ravenousActive = $state(true);
  /** Target carries Bleed status — gates Sadist / Perfect Sadist (chest amp WD vs bleeding)
   *  and similar bleed-conditional bonuses. Default false; auto-effective true in formula
   *  when build contains a guaranteed bleed-applying source (Caduceus/Regulus/Bleeding Edge etc.). */
  let targetBleeding = $state(false);
  let fullArmor = $state(true);
  /** Active stacks of 4pc set bonuses (keyed by setId). */
  let setStacks: Record<string, number> = $state({});
  /** Set chest talent equipped per set (raises maxChest cap). */
  let setChestTalent: Record<string, boolean> = $state({});
  /** Set backpack talent equipped per set (switches perStack → perStackBp). */
  let setBpTalent: Record<string, boolean> = $state({});
  /** Exotic gear talent active (per slot). */
  let exoticActive: Record<string, boolean> = $state({});
  /** Input mode: 'gear' — auto from gear, 'stats' — user enters raw % */
  const DEFAULT_STATS = {
    wd: 90, chc: 60, chd: 150, hsd: 50,
    rof: 0, mag: 0, reload: 0, ooc: 0,
    dta: 15, dth: 15,
    amp: 0,
    wd_ar: 0, wd_smg: 0, wd_lmg: 0, wd_mmr: 0,
    wd_rifle: 0, wd_shotgun: 0, wd_pistol: 0,
  };
  let inputMode: 'gear' | 'stats' = $state((() => {
    if (typeof localStorage === 'undefined') return 'gear';
    try { return (localStorage.getItem('divcalc:input-mode') as 'gear' | 'stats') || 'gear'; } catch { return 'gear'; }
  })());
  let manualStats = $state((() => {
    if (typeof localStorage === 'undefined') return { ...DEFAULT_STATS };
    try {
      const raw = localStorage.getItem('divcalc:manual-stats');
      if (raw) return { ...DEFAULT_STATS, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return { ...DEFAULT_STATS };
  })());
  let expertiseGrade = $state(0);
  let headshotChancePct = $state(0);
  /**
   * Manual stat overrides — PRO mode point-fix: when calculator-computed value
   * disagrees with what the user actually sees in-game, override this single
   * stat without switching to full Stats mode. Applied AFTER all gear/talent/spec/scp
   * computations replace (not add to) computed value.
   * Keys: additive stats (`wd`, `wd_<class>`, `chc`, `chd`, `hsd`, `rof`, `mag`,
   * `mag_flat`, `reload`, `handling`, `dth`, `dta`, `ooc`) and weapon-level
   * (`baseDamage`, `magazine`, `rpm`, `reloadSeconds`).
   */
  let manualOverrides: Record<string, number> = $state({});
  /**
   * Verify mode (PRO): user types in-game values next to calc values.
   * NOT applied to formula — pure comparison. Stored as Record<stat, in-game value>.
   * Same key whitelist as manualOverrides (additive + weapon-level).
   */
  let verifyValues: Record<string, number> = $state({});
  /** Recombinator: 3 free slots where user sets (stat, percent) directly. */
  const DEFAULT_REC: Array<{ stat: AttrStat | null; value: number }> = [
    { stat: null, value: 0 },
    { stat: null, value: 0 },
    { stat: null, value: 0 },
  ];
  let recombinator: Array<{ stat: AttrStat | null; value: number }> = $state((() => {
    if (typeof localStorage === 'undefined') return DEFAULT_REC.map(x => ({ ...x }));
    try {
      const raw = localStorage.getItem('divcalc:rec-stats');
      if (raw) { const parsed = JSON.parse(raw); if (Array.isArray(parsed) && parsed.length === 3) return parsed; }
    } catch { /* ignore */ }
    return DEFAULT_REC.map(x => ({ ...x }));
  })());

  return {
    get weaponId() {
      return weaponId;
    },
    set weaponId(v: string | null) {
      weaponId = v;
      weaponTalentActive = false;
      customWeaponTalentId = null;
      weaponAttrSelected = null;
    },
    get weaponAttrSelected() { return weaponAttrSelected; },
    set weaponAttrSelected(v: string | null) { weaponAttrSelected = v; },
    get weaponTalentActive() {
      return weaponTalentActive;
    },
    set weaponTalentActive(v: boolean) {
      weaponTalentActive = v;
    },
    get weaponTalentStacks() {
      return weaponTalentStacks;
    },
    set weaponTalentStacks(v: number) {
      weaponTalentStacks = Math.max(0, v);
    },
    get customWeaponTalentId() {
      return customWeaponTalentId;
    },
    set customWeaponTalentId(v: string | null) {
      customWeaponTalentId = v;
    },
    get gear() {
      return gear;
    },
    get expertiseGrade() {
      return expertiseGrade;
    },
    set expertiseGrade(v: number) {
      // TU22+ raised cap to 31 (Elevated). G31 unlocks talent/mod rerolls + per-slot bonuses.
      expertiseGrade = Math.max(0, Math.min(31, v));
    },
    get headshotChancePct() {
      return headshotChancePct;
    },
    set headshotChancePct(v: number) {
      headshotChancePct = Math.max(0, Math.min(100, v));
    },
    setSlotBrand(slot: SlotKey, brandId: string | null) {
      gear[slot] = { ...gear[slot], brandId, setId: brandId ? null : gear[slot].setId, namedId: brandId ? null : gear[slot].namedId };
    },
    setSlotSet(slot: SlotKey, setId: string | null) {
      gear[slot] = { ...gear[slot], setId, brandId: setId ? null : gear[slot].brandId, namedId: setId ? null : gear[slot].namedId };
    },
    setSlotNamed(slot: SlotKey, namedId: string | null) {
      gear[slot] = { ...gear[slot], namedId, brandId: namedId ? null : gear[slot].brandId, setId: namedId ? null : gear[slot].setId };
    },
    setSlotCore(slot: SlotKey, coreStat: GearSlot['coreStat']) {
      gear[slot] = { ...gear[slot], coreStat };
    },
    setSlotAttr(slot: SlotKey, which: 'attr1' | 'attr2' | 'modAttr', stat: AttrStat | null) {
      gear[slot] = { ...gear[slot], [which]: stat };
    },
    setSlotPrototype(slot: SlotKey, isPrototype: boolean) {
      gear[slot] = { ...gear[slot], isPrototype, prototypeAugmentId: isPrototype ? gear[slot].prototypeAugmentId : null };
    },
    setSlotPrototypeAugment(slot: SlotKey, augmentId: string | null) {
      gear[slot] = { ...gear[slot], prototypeAugmentId: augmentId };
    },
    get chestTalentId() { return chestTalentId; },
    set chestTalentId(v: string | null) {
      chestTalentId = v;
      if (!v) chestTalentActive = false;
    },
    get chestTalentActive() { return chestTalentActive; },
    set chestTalentActive(v: boolean) { chestTalentActive = v; },
    get backpackTalentId() { return backpackTalentId; },
    set backpackTalentId(v: string | null) {
      backpackTalentId = v;
      if (!v) backpackTalentActive = false;
    },
    get backpackTalentActive() { return backpackTalentActive; },
    set backpackTalentActive(v: boolean) { backpackTalentActive = v; },
    get shdWatchActive() { return shdWatchActive; },
    set shdWatchActive(v: boolean) { shdWatchActive = v; },
    get weaponMods() { return weaponMods; },
    setWeaponMod(slot: ModSlot, id: string | null) {
      weaponMods = { ...weaponMods, [slot]: id };
    },
    get specializationId() { return specializationId; },
    set specializationId(v: string | null) { specializationId = v; },
    get specClassPicks() { return specClassPicks; },
    toggleSpecClassPick(cls: string) {
      if (specClassPicks.includes(cls)) {
        specClassPicks = specClassPicks.filter((c) => c !== cls);
      } else if (specClassPicks.length < 3) {
        specClassPicks = [...specClassPicks, cls];
      }
    },
    setSpecClassPicks(picks: string[]) {
      const allowed = new Set(['ar', 'smg', 'lmg', 'mmr', 'rifle', 'shotgun', 'pistol']);
      specClassPicks = picks.filter((p) => allowed.has(p)).slice(0, 3);
    },
    get specMmrRifleHsd() { return specMmrRifleHsd; },
    set specMmrRifleHsd(v: boolean) { specMmrRifleHsd = v; },
    get activeSpecPerks() { return activeSpecPerks; },
    toggleSpecPerk(id: string) {
      activeSpecPerks = activeSpecPerks.includes(id)
        ? activeSpecPerks.filter(p => p !== id)
        : [...activeSpecPerks, id];
    },
    get groupSize() { return groupSize; },
    set groupSize(v: number) { groupSize = Math.max(1, Math.min(4, v)); },
    get targetStatus() { return targetStatus; },
    set targetStatus(v: typeof targetStatus) { targetStatus = v; },
    get targetPulsed() { return targetPulsed; },
    set targetPulsed(v: boolean) { targetPulsed = v; },
    get targetMarked() { return targetMarked; },
    set targetMarked(v: boolean) { targetMarked = v; },
    get firstShotMode() { return firstShotMode; },
    set firstShotMode(v: boolean) { firstShotMode = v; },
    get harrierPerfectCount() { return harrierPerfectCount; },
    set harrierPerfectCount(v: number) { harrierPerfectCount = Math.max(0, Math.min(3, Math.round(v))); },
    get ravenousActive() { return ravenousActive; },
    set ravenousActive(v: boolean) { ravenousActive = v; },
    get targetBleeding() { return targetBleeding; },
    set targetBleeding(v: boolean) { targetBleeding = v; },
    get fullArmor() { return fullArmor; },
    set fullArmor(v: boolean) { fullArmor = v; },
    get setStacks() { return setStacks; },
    setSetStacks(setId: string, n: number) {
      setStacks = { ...setStacks, [setId]: Math.max(0, n) };
    },
    get setChestTalent() { return setChestTalent; },
    setSetChestTalent(setId: string, v: boolean) {
      setChestTalent = { ...setChestTalent, [setId]: v };
    },
    get setBpTalent() { return setBpTalent; },
    setSetBpTalent(setId: string, v: boolean) {
      setBpTalent = { ...setBpTalent, [setId]: v };
    },
    get exoticActive() { return exoticActive; },
    setExoticActive(slot: string, v: boolean) {
      exoticActive = { ...exoticActive, [slot]: v };
    },
    get inputMode() { return inputMode; },
    set inputMode(v: 'gear' | 'stats') {
      inputMode = v;
      try { localStorage.setItem('divcalc:input-mode', v); } catch { /* ignore */ }
    },
    get manualStats() { return manualStats; },
    setManualStat(key: keyof typeof manualStats, v: number) {
      manualStats = { ...manualStats, [key]: Math.max(0, v) };
      try { localStorage.setItem('divcalc:manual-stats', JSON.stringify(manualStats)); } catch { /* ignore */ }
    },
    get recombinator() { return recombinator; },
    setRecombinatorSlot(idx: number, stat: AttrStat | null, value: number) {
      if (idx < 0 || idx > 2) return;
      const next = [...recombinator];
      next[idx] = { stat, value: Math.max(0, value) };
      recombinator = next;
      try { localStorage.setItem('divcalc:rec-stats', JSON.stringify(recombinator)); } catch { /* ignore */ }
    },
    get manualOverrides() { return manualOverrides; },
    setManualOverride(stat: string, value: number | null) {
      const next = { ...manualOverrides };
      if (value == null || !Number.isFinite(value)) {
        delete next[stat];
      } else {
        next[stat] = value;
      }
      manualOverrides = next;
    },
    clearManualOverrides() {
      manualOverrides = {};
    },
    get verifyValues() { return verifyValues; },
    setVerifyValue(stat: string, value: number | null) {
      const next = { ...verifyValues };
      if (value == null || !Number.isFinite(value)) {
        delete next[stat];
      } else {
        next[stat] = value;
      }
      verifyValues = next;
    },
    clearVerifyValues() {
      verifyValues = {};
    },
    reset() {
      weaponId = null;
      weaponTalentActive = false;
      weaponTalentStacks = 0;
      customWeaponTalentId = null;
      weaponAttrSelected = null;
      for (const k of SLOT_KEYS) gear[k] = emptySlot();
      chestTalentId = null;
      chestTalentActive = false;
      backpackTalentId = null;
      backpackTalentActive = false;
      shdWatchActive = true;
      weaponMods = { optic: null, muzzle: null, underbarrel: null, magazine: null };
      specializationId = null;
      specClassPicks = [];
      specMmrRifleHsd = false;
      groupSize = 1;
      targetStatus = 'none';
      targetPulsed = false;
      targetMarked = false;
      firstShotMode = false;
      harrierPerfectCount = 3;
      ravenousActive = true;
      targetBleeding = false;
      fullArmor = true;
      setStacks = {};
      setChestTalent = {};
      setBpTalent = {};
      exoticActive = {};
      expertiseGrade = 0;
      headshotChancePct = 0;
      manualOverrides = {};
      verifyValues = {};
      // inputMode and manualStats preserved intentionally — user preference
    },
  };
}

export type BuildState = ReturnType<typeof createBuildState>;

export interface BuildSummary {
  dps: DpsOutput;
  dpsInput: DpsInput;
  additive: {
    wd: number;
    chc: number;
    chd: number;
    hsd: number;
    rof: number;
    mag: number;
    reload: number;
    ooc: number;
    dta: number;
    dth: number;
  };
  setCounts: Record<string, number>;
  brandCounts: Record<string, number>;
  /** DPS simulated at time points (seconds) with stack ramp-up */
  ramp: Array<{ t: number; dps: number; stacks: Record<string, number> }>;
  /** Burn damage (DoT) for weapons that apply burn status (Iron Lung, Pyromaniac, etc). */
  burn?: {
    dpsPerTick: number;
    duration: number;
    totalPerApplication: number;
    statusEffectsPct: number;
    burnDurationPct: number;
  };
  /** Bleed damage (DoT) for weapons that apply bleed (Scorpio HS mark, Diamondback reload mark, Razorback, Regulus HS-kill, etc). */
  bleed?: {
    dpsPerTick: number;
    duration: number;
    totalPerApplication: number;
    statusEffectsPct: number;
    bleedDurationPct: number;
  };
  /** Poison damage (DoT) for weapons that apply poison (Pestilence Plague, Bighorn shield-break, Chem Oxidizer, etc). Does NOT scale with Status Effects stat. */
  poison?: {
    dpsPerTick: number;
    duration: number;
    totalPerApplication: number;
    poisonDurationPct: number;
  };
}

// Base Burn DPS coefficient at level 40 (max WT).
// Formula from game data (attribute_dictionary.mdict):
//   Burn_DPS = SkillCurveFinal × 40 × (1 + Status_Effects%)
//   Burn_Duration = 5 + (5 × Burn_Duration%)
// SkillCurveFinal at level 40 ≈ 300 (community-verified).
const SKILL_CURVE_LVL40 = 300;
// Status DoT coefficients from raw/status_damage_multipliers.json (TU22.1 / Y8S1).
// Each coefficient is applied as: dpsPerTick = SKILL_CURVE_LVL40 * COEFF * (1 + statusEffectsPct/100).
// Burn: scales with status_effects stat. Bleed: scales with status_effects. Poison: does NOT scale (raw flag «Не скалируется модбонусом»).
const BURN_BASE_COEFF = 40;
const BURN_BASE_DURATION = 5;
const BLEED_BASE_COEFF = 35;
const BLEED_BASE_DURATION = 7;
const POISON_BASE_COEFF = 20;
const POISON_BASE_DURATION = 4;

/** Simulate stack count at time t based on trigger type and cap. */
function stacksAtTime(trigger: string, maxStacks: number, t: number): number {
  // approximation (v1 uses more complex decay, this is simplified)
  const rate: Record<string, number> = {
    hit: 8,     // shots per sec approx for SMG/AR
    hs: 2,      // headshots per sec
    kill: 0.33,
    tempo: 20,
    proc: 1,
    shot_cover: 8,
    hs_kill: 0.15,
  };
  const r = rate[trigger] ?? 1;
  return Math.min(Math.floor(r * t), maxStacks);
}

/** Stat keys that PRO-mode `manualOverrides` may replace inside `additive`. */
export const ADDITIVE_OVERRIDE_KEYS = new Set<string>([
  'wd', 'chc', 'chd', 'hsd', 'rof', 'mag', 'mag_flat', 'reload', 'handling',
  'dth', 'dta', 'ooc',
  'wd_ar', 'wd_smg', 'wd_lmg', 'wd_mmr', 'wd_rifle', 'wd_shotgun', 'wd_pistol',
]);

/** Weapon-level fields that PRO-mode `manualOverrides` may replace in `dpsInput.weapon`. */
export const WEAPON_OVERRIDE_KEYS = new Set<string>([
  'baseDamage', 'magazine', 'rpm', 'reloadSeconds',
]);

/** Per-key reasonable bounds used by URL decode + UI input. */
export const OVERRIDE_BOUNDS: Record<string, [number, number]> = {
  wd: [0, 1000], chc: [0, 100], chd: [0, 1000], hsd: [0, 1000],
  rof: [-50, 200], mag: [-50, 500], mag_flat: [0, 500], reload: [-50, 500],
  handling: [0, 500], dth: [0, 500], dta: [0, 500], ooc: [0, 500],
  wd_ar: [0, 500], wd_smg: [0, 500], wd_lmg: [0, 500], wd_mmr: [0, 500],
  wd_rifle: [0, 500], wd_shotgun: [0, 500], wd_pistol: [0, 500],
  baseDamage: [0, 5_000_000], magazine: [1, 500], rpm: [1, 2000], reloadSeconds: [0.1, 20],
};

export function isOverrideKeyAllowed(key: string): boolean {
  return ADDITIVE_OVERRIDE_KEYS.has(key) || WEAPON_OVERRIDE_KEYS.has(key);
}

export function clampOverride(key: string, value: number): number {
  const b = OVERRIDE_BOUNDS[key];
  if (!b) return value;
  return Math.min(Math.max(value, b[0]), b[1]);
}

const BRAND_STAT_KEYS = new Set<string>([
  'wd', 'chc', 'chd', 'hsd', 'rof', 'mag', 'reload', 'handling', 'ooc', 'dta', 'dth', 'armor',
  'armor_on_kill', 'health', 'skill_dmg', 'skill_haste', 'status_effects', 'elite',
  // Weapon-class-specific damage bonuses (matched to weapon category in computeBuild)
  'wd_ar', 'wd_smg', 'wd_lmg', 'wd_mmr', 'wd_rifle', 'wd_shotgun', 'wd_pistol',
  // Raw brand stat names from data — mapped to wd_<class> in addBrandSetBonus()
  'ar_dmg', 'smg_dmg', 'lmg_dmg', 'mmr_dmg', 'rifle_dmg', 'shotgun_dmg', 'pistol_dmg',
  'explosive_dmg',
]);

const CLASS_DMG_TO_WD_KEY: Record<string, string> = {
  ar_dmg: 'wd_ar',
  smg_dmg: 'wd_smg',
  lmg_dmg: 'wd_lmg',
  mmr_dmg: 'wd_mmr',
  rifle_dmg: 'wd_rifle',
  shotgun_dmg: 'wd_shotgun',
  pistol_dmg: 'wd_pistol',
};

/** Named-gear ids whose `activeBonuses` are NOT auto-applied in the slot loop —
 *  they go through `applyGatedNamedActiveBonuses()` below for context-aware scaling. */
const GATED_ACTIVE_BONUS_NAMED = new Set<string>([
  'dodge_city_gunslingers_holster',
  'centurions_scabbard',
  'overdogs',
  'waveform',
  'harrier_pride',
]);

// Named gear talent bonuses at PEAK (v1-style "full stacks" display).
// Equalizer: "Versatile" — crit hits grant +1% WD for 10s, up to 100 stacks → +100% WD amplified.
// Deathgrips: +8% DtA + +8% DtH + crit talent.
// Ceska Claws: +20% CHD.
// etc. Mirror v1 peak values.
const NAMED_TALENT_BONUSES: Record<string, Array<{ stat: string; value: number; amp?: boolean }>> = {
  // Equalizer — "Идеальное тотальное уничтожение" (Perfect Obliterate):
  // Crit hits +1% WD for 10s, stacks up to 24. At peak = +24% WD AMPLIFIED (own multiplier).
  // Verified: with amp — matches in-game bullet damage 1:1 (St. Elmo + Strikers 4pc).
  equalizer: [{ stat: 'wd', value: 24, amp: true }],
  deathgrips: [{ stat: 'dta', value: 8 }, { stat: 'dth', value: 8 }],
  contractor_s_gloves: [{ stat: 'wd', value: 15 }],
  fox_s_prayer: [{ stat: 'ooc', value: 8 }],
  the_chatterbox: [{ stat: 'rof', value: 20 }],
  ninjabike_messenger_kneepads: [{ stat: 'wd', value: 10 }],
  hollow_man: [{ stat: 'wd', value: 25, amp: true }],
  wyvern_wear_mask: [{ stat: 'chd', value: 20 }],
  picaro_s_holster: [{ stat: 'wd', value: 15 }],
};

export function computeBuild(state: BuildState, data: GameData): BuildSummary {
  const additive = {
    wd: 0,
    chc: 0,
    chd: 0,
    hsd: 0,
    rof: 0,
    mag: 0,
    reload: 0,
    ooc: 0,
    dta: 0,
    dth: 0,
  } as BuildSummary['additive'] & Record<string, number>;

  const amplifiedMultipliers: number[] = [];

  // Dynamic talent-stack WD (Equalizer Obliterate, etc.) — NOT shown in weapon stat panel,
  // only applied to DPS calc. Game separates these from gear stats.
  let talentStackWd = 0;

  const setCounts: Record<string, number> = {};
  const brandCounts: Record<string, number> = {};

  // STATS mode: user enters values directly, gear is ignored for DPS
  if (state.inputMode === 'stats') {
    const ms = state.manualStats;
    Object.assign(additive, {
      wd: ms.wd,
      chc: ms.chc,
      chd: ms.chd,
      hsd: ms.hsd,
      rof: ms.rof,
      mag: ms.mag,
      reload: ms.reload,
      ooc: ms.ooc,
      dta: ms.dta,
      dth: ms.dth,
    });

    const weapon = state.weaponId ? data.byId.weapon.get(state.weaponId) : null;
    const typeWd = weapon ? (ms as Record<string, number>)[`wd_${weapon.category}`] ?? 0 : 0;
    const ampPct = (ms as Record<string, number>).amp ?? 0;
    const magSm = (1 + additive.mag / 100);
    const reloadSm = 1 / (1 + additive.reload / 100);
    const dpsInput: DpsInput = {
      weapon: weapon
        ? {
            baseDamage: weapon.baseDamage,
            rpm: weapon.rpm * (1 + additive.rof / 100),
            magazine: Math.max(1, Math.round(weapon.magazine * magSm)),
            reloadSeconds: Math.max(0.1, weapon.reloadSeconds * reloadSm),
            headshotMultiplier: weapon.headshotMultiplier,
          }
        : { baseDamage: 0, rpm: 0, magazine: 1, reloadSeconds: 1, headshotMultiplier: 1.5 },
      additive: { weaponDamagePct: additive.wd, weaponTypeDamagePct: typeWd, additiveTalentsPct: 0 },
      amplified: { multipliers: ampPct > 0 ? [ampPct] : [] },
      crit: { chcPct: additive.chc, chdPct: additive.chd, hsdPct: additive.hsd, headshotChancePct: state.headshotChancePct },
      target: { damageToArmorPct: additive.dta, damageToHealthPct: additive.dth, damageOutOfCoverPct: additive.ooc, fullArmor: state.fullArmor },
      expertise: { grade: state.expertiseGrade },
    };
    return {
      dps: calculateDps(dpsInput),
      dpsInput,
      additive,
      setCounts,
      brandCounts,
      ramp: [],
    };
  }

  for (const slotKey of SLOT_KEYS) {
    const slot = state.gear[slotKey];
    if (slot.setId) setCounts[slot.setId] = (setCounts[slot.setId] || 0) + 1;
    if (slot.brandId) brandCounts[slot.brandId] = (brandCounts[slot.brandId] || 0) + 1;
    // Named gear contributes via its brand + fixed attrs (locked)
    if (slot.namedId) {
      const ng = data.byId.namedGear.get(slot.namedId);
      if (ng) {
        if (ng.brand) brandCounts[ng.brand] = (brandCounts[ng.brand] || 0) + 1;
        for (const { stat, value } of ng.fixedAttrs) {
          (additive as Record<string, number>)[stat] = ((additive as Record<string, number>)[stat] || 0) + value;
        }
        // Named/Exotic active talent bonuses — auto-on (peak), unless this exotic
        // has a gating rule applied below (see GATED_ACTIVE_BONUS_NAMED).
        // GATED items: dodge_city_gunslingers_holster (single-shot proc),
        //              centurions_scabbard (next-3-shots after reload),
        //              overdogs (group-proximity scaling),
        //              waveform (3-stack peak + SMG/AR category gate),
        //              harrier_pride (Perfect-talent count scaling).
        // For these we apply the bonus in a second pass that has access to weapon and conditions.
        if (ng.activeBonuses && !GATED_ACTIVE_BONUS_NAMED.has(ng.id)) {
          for (const b of ng.activeBonuses) {
            if (b.amp) {
              amplifiedMultipliers.push(b.value);
            } else {
              (additive as Record<string, number>)[b.stat] =
                ((additive as Record<string, number>)[b.stat] || 0) + b.value;
            }
          }
        }
        // Named talent stacks (Equalizer Obliterate = +24% WD at peak) — DYNAMIC stacks,
        // NOT shown in game's "Weapon damage" stat panel, only active during combat.
        // We track them separately in talentStackWd to apply only in DPS calc.
        const extra = NAMED_TALENT_BONUSES[ng.id];
        if (extra) {
          for (const b of extra) {
            if (b.amp) amplifiedMultipliers.push(b.value);
            else if (b.stat === 'wd') {
              talentStackWd += b.value;
            } else {
              (additive as Record<string, number>)[b.stat] =
                ((additive as Record<string, number>)[b.stat] || 0) + b.value;
            }
          }
        }
      }
    }
    // Effective core stat resolution:
    // - For named/exotic gear: the named item's `core` field overrides slot.coreStat.
    //   yellow/blue cores DON'T contribute +15% WD; only red ('wd') does.
    //   'variable' (Investor/NinjaBike) currently treated as user-config: respects slot.coreStat.
    // - For regular gear: respect user-picked slot.coreStat.
    const slotNamedItem = slot.namedId ? data.byId.namedGear.get(slot.namedId) : null;
    let effectiveCore: string = slot.coreStat;
    if (slotNamedItem) {
      if (slotNamedItem.core === 'variable') {
        // Investor / NinjaBike — respect user pick (slot.coreStat)
      } else {
        effectiveCore = slotNamedItem.core; // 'wd' | 'armor' | 'skill_tier'
      }
    }
    if (effectiveCore === 'wd') additive.wd += 15;

    // Attribute rolls (max values by default).
    // Named gear: fixedAttrs replace the corresponding slot — don't double-count.
    // Set gear: only ONE attribute slot (attr2 is reserved for the set bonus).
    // 3-core layout (Memento/Harrier/NinjaBike BP): no minor secondaries — fixedAttrs cover all.
    const isThreeCore = slotNamedItem?.layoutType === '3core';
    const namedFixedCount = slotNamedItem?.fixedAttrs.length ?? 0;
    const attr1Locked = isThreeCore || namedFixedCount >= 1;
    const attr2Locked = isThreeCore || namedFixedCount >= 2;
    if (slot.attr1 && !attr1Locked) {
      additive[slot.attr1] = (additive[slot.attr1] || 0) + ATTR_DEFAULTS[slot.attr1];
    }
    if (slot.attr2 && !attr2Locked && !slot.setId) {
      additive[slot.attr2] = (additive[slot.attr2] || 0) + ATTR_DEFAULTS[slot.attr2];
    }
    if (slot.modAttr && MOD_SLOT_KEYS.has(slotKey)) {
      const mod = findGearMod(slot.modAttr);
      if (mod && (mod.stat === 'chc' || mod.stat === 'chd' || mod.stat === 'hsd' || mod.stat === 'skill_haste' || mod.stat === 'skill_duration' || mod.stat === 'repair_skills')) {
        (additive as Record<string, number>)[mod.stat] =
          ((additive as Record<string, number>)[mod.stat] || 0) + mod.value;
      }
    }
  }

  const applyBrandBonus = (statRaw: string, value: number) => {
    if (!BRAND_STAT_KEYS.has(statRaw)) return;
    const stat = CLASS_DMG_TO_WD_KEY[statRaw] ?? statRaw;
    (additive as Record<string, number>)[stat] =
      ((additive as Record<string, number>)[stat] || 0) + value;
  };

  for (const [setId, count] of Object.entries(setCounts)) {
    const gs = data.byId.set.get(setId);
    if (!gs) continue;
    for (const { pieces, bonus } of gs.numericBonuses) {
      if (count >= pieces) applyBrandBonus(bonus.stat, bonus.value);
    }
  }

  // Special: Core Strength 4pc — «Core Exercise» talent.
  // For each Core on your gear pieces, receive 40% (75% with Inner Core chest)
  // of the OTHER two Cores' standard bonuses.
  // Per-core base values (from raw game files):
  //   red (wd) = 15% WD
  //   blue (armor) = 170,000 flat armor (modeled as +X% armor approximation)
  //   yellow (skill_tier) = 15% skill efficiency (≈ skill_haste)
  if ((setCounts['core_strength'] ?? 0) >= 4) {
    const isInnerCore = state.gear.chest.setId === 'core_strength';
    const conv = isInnerCore ? 0.75 : 0.4;
    let nRed = 0, nBlue = 0, nYellow = 0;
    for (const slotKey of SLOT_KEYS) {
      const c = state.gear[slotKey].coreStat;
      if (c === 'wd') nRed++;
      else if (c === 'armor') nBlue++;
      else if (c === 'skill_tier') nYellow++;
    }
    // Each red core grants conv × (blue_value + yellow_value) extras
    // Each blue core grants conv × (red + yellow)
    // Each yellow grants conv × (red + blue)
    const RED_WD = 15;       // % WD per red core (game baseline)
    const BLUE_ARMOR_PCT = 12.5; // ≈ % armor per blue core (170k flat ≈ 12.5% at GS500)
    const YELLOW_SKILL = 15; // % skill efficiency per yellow core
    // Bonus WD = (blue + yellow cores) × conv × RED_WD
    const bonusWd = (nBlue + nYellow) * conv * RED_WD;
    // Bonus armor = (red + yellow) × conv × BLUE_ARMOR_PCT
    const bonusArmor = (nRed + nYellow) * conv * BLUE_ARMOR_PCT;
    // Bonus skill_haste (≈ efficiency) = (red + blue) × conv × YELLOW_SKILL
    const bonusSkill = (nRed + nBlue) * conv * YELLOW_SKILL;
    additive.wd += bonusWd;
    (additive as Record<string, number>).armor =
      ((additive as Record<string, number>).armor || 0) + bonusArmor;
    (additive as Record<string, number>).skill_haste =
      ((additive as Record<string, number>).skill_haste || 0) + bonusSkill;
  }

  for (const [brandId, count] of Object.entries(brandCounts)) {
    const br = data.byId.brand.get(brandId);
    if (!br) continue;
    for (const { pieces, bonus } of br.bonuses) {
      if (count >= pieces) applyBrandBonus(bonus.stat, bonus.value);
    }
  }

  const weapon = state.weaponId ? data.byId.weapon.get(state.weaponId) : null;

  // Second pass — gated named-gear active bonuses (need weapon / condition context).
  // Each ID has its own scaling rule:
  //   dodge_city_gunslingers_holster: single-shot proc → averaged across mag (firstShotMode=peak).
  //   centurions_scabbard: next-3-shots after reload → 3 / mag averaging (firstShotMode=peak).
  //   overdogs: scales with allies <10m → (groupSize-1)/3, max at 4-man.
  //   waveform: 3-stack peak (×3) + SMG/AR category gate.
  //   harrier_pride: linear with state.harrierPerfectCount (0..3) / 3.
  for (const slotKey of SLOT_KEYS) {
    const slot = state.gear[slotKey];
    if (!slot.namedId || !GATED_ACTIVE_BONUS_NAMED.has(slot.namedId)) continue;
    const ng = data.byId.namedGear.get(slot.namedId);
    if (!ng?.activeBonuses) continue;
    let mul = 1;
    let active = true;
    if (slot.namedId === 'dodge_city_gunslingers_holster') {
      mul = state.firstShotMode ? 1 : (1 / Math.max(1, weapon?.magazine ?? 30));
    } else if (slot.namedId === 'centurions_scabbard') {
      mul = state.firstShotMode ? 1 : (3 / Math.max(1, weapon?.magazine ?? 30));
    } else if (slot.namedId === 'overdogs') {
      mul = Math.max(0, Math.min(3, state.groupSize - 1)) / 3;
    } else if (slot.namedId === 'waveform') {
      if (weapon && (weapon.category === 'smg' || weapon.category === 'ar')) {
        mul = 3;  // 3 stacks at peak
      } else {
        active = false;
      }
    } else if (slot.namedId === 'harrier_pride') {
      mul = Math.max(0, Math.min(3, state.harrierPerfectCount)) / 3;
    }
    if (!active || mul <= 0) continue;
    for (const b of ng.activeBonuses) {
      const value = b.value * mul;
      if (b.amp) {
        amplifiedMultipliers.push(value);
      } else {
        (additive as Record<string, number>)[b.stat] =
          ((additive as Record<string, number>)[b.stat] || 0) + value;
      }
    }
  }

  // Weapon intrinsic attributes. Convention: pos[0]+[1] fixed, pos[2] user-selectable
  // (default = the value shipped in data; user can swap via state.weaponAttrSelected).
  if (weapon?.intrinsicAttrs && weapon.intrinsicAttrs.length > 0) {
    const ia = weapon.intrinsicAttrs;
    for (let i = 0; i < Math.min(2, ia.length); i++) {
      (additive as Record<string, number>)[ia[i].stat] =
        ((additive as Record<string, number>)[ia[i].stat] || 0) + ia[i].value;
    }
    if (ia.length >= 3) {
      const slot3 = resolveSlot3(weapon, state.weaponAttrSelected);
      if (slot3) {
        (additive as Record<string, number>)[slot3.stat] =
          ((additive as Record<string, number>)[slot3.stat] || 0) + slot3.value;
      }
    }
  }

  // Exotic weapons have BUILT-IN mods (Проводник/Изоляция/Перезаряд). Player cannot change them
  // like on base/named weapons. Auto-apply as if equipped.
  if (weapon && weapon.kind === 'exotic' && Array.isArray((weapon as unknown as Record<string, unknown>).builtInMods)) {
    const builtIn = (weapon as unknown as {
      builtInMods: Array<{ slot: string; stat: string; value: number }>
    }).builtInMods;
    for (const b of builtIn) {
      if (b.stat === 'mag_add') {
        (additive as Record<string, number>).mag_flat =
          ((additive as Record<string, number>).mag_flat || 0) + b.value;
      } else {
        (additive as Record<string, number>)[b.stat] =
          ((additive as Record<string, number>)[b.stat] || 0) + b.value;
      }
    }
  }

  // Weapon talent application
  // For named/exotic weapons: use weapon.talentId (locked)
  // For base weapons: use state.customWeaponTalentId (user-chosen)
  // Auto-activate at peak: named/exotic talents apply automatically, base only if user picked a talent.
  // Special: `the_ravenous` (Geri & Freki) — proc-based +500% wd, user gates via state.ravenousActive.
  const ravenousProcGated = weapon?.id === 'the_ravenous' && !state.ravenousActive;
  const effectiveTalentId = ravenousProcGated
    ? null
    : (weapon?.kind === 'base' ? state.customWeaponTalentId : weapon?.talentId);
  const weaponTalentOn = !!effectiveTalentId && (weapon?.kind !== 'base' || state.weaponTalentActive);
  if (effectiveTalentId && weaponTalentOn) {
    const talent = data.byId.talent.get(effectiveTalentId);
    if (talent) {
      for (const { stat, value } of talent.bonuses) {
        if (talent.bonusType === 'amplified') {
          amplifiedMultipliers.push(value);
        } else if (stat === 'wd' && weapon?.kind !== 'base') {
          talentStackWd += value;
        } else if (stat in additive) {
          (additive as Record<string, number>)[stat] =
            ((additive as Record<string, number>)[stat] || 0) + value;
        }
      }
    }
  }

  // Exotic weapon talent types (amp/kill/stacks/shot_cover/hs_kill/swap_in/conditional/no_reload)
  // Auto-max stacks — calculator always shows PEAK DPS (with talent at full).
  if (weapon && weaponTalentOn && weapon.talType && typeof weapon.talBonus === 'number') {
    const talType = weapon.talType;
    const talBonus = weapon.talBonus;
    const talMax = weapon.talMax || 1;
    const stacks = talMax;

    let appliedWd = 0;
    if (talType === 'amp' || talType === 'swap_in' || talType === 'conditional') {
      // Constant bonus when active
      appliedWd = talBonus;
    } else if (talType === 'kill' || talType === 'stacks' || talType === 'shot_cover' || talType === 'hs_kill') {
      // Per-stack scaling: stacks / max * tal_bonus (or stacks * per-stack if tal_max is large)
      const capped = Math.min(stacks, talMax);
      appliedWd = talMax > 0 ? (capped / talMax) * talBonus : 0;
    }
    if (appliedWd > 0) {
      additive.wd += appliedWd;
    }
  }

  // Bleed-conditional bonuses (Sadist / Perfect Sadist) gate on effective bleed status:
  // user toggle OR build contains a guaranteed bleed-applier — `weapon.appliesBleed=true`
  // (Caduceus / Regulus / Agitator) OR `Ongoing Directive` 4pc set equipped.
  const buildAppliesBleed =
    !!(weapon as unknown as { appliesBleed?: boolean })?.appliesBleed ||
    (setCounts['ongoing_directive'] ?? 0) >= 4;
  const effectiveBleeding = state.targetBleeding || buildAppliesBleed;

  // Gear talents (chest + backpack). Auto-active when id is set — calculator shows peak.
  const applyGearTalent = (id: string | null) => {
    if (!id) return;
    const t = findGearTalentBonus(id);
    if (!t) return;
    if (t.requiresBleeding && !effectiveBleeding) return; // gated — bleed not on target
    for (const { stat, value } of t.bonuses) {
      if (t.bonusType === 'amplified') {
        amplifiedMultipliers.push(value);
      } else {
        (additive as Record<string, number>)[stat] =
          ((additive as Record<string, number>)[stat] || 0) + value;
      }
    }
  };
  applyGearTalent(state.chestTalentId);
  applyGearTalent(state.backpackTalentId);

  // Base game values shown in weapon stat panel (every player has these by default):
  //   Base CHC = 0% (no hidden baseline)
  //   Base CHD = 25% (all players have this regardless of gear)
  // User verified via in-game screenshot: Богомол sniper with only Watch (+20 CHD) shows 45% CHD total.
  additive.chd += 25;

  // SHD Watch (Часы Кинера) — 4 trees (120 points each, 20/attr max).
  // Когда часы вкачаны (shdWatchActive=true) применяем суммарный effect:
  //   Offensive: WD +10%, CHC +10%, CHD +20%, HSD +20%, OoC +10%, Explosive +15%
  //   Handling:  ReloadSpeed +10%, WeaponHandling +10%
  // Раньше эти бонусы дублировались (always-on baseline + shdWatchActive) — фикс убрал двойной счёт.
  if (state.shdWatchActive) {
    additive.wd += 10;
    additive.chc += 10;
    additive.chd += 20;
    additive.hsd += 20;
    (additive as Record<string, number>).handling = ((additive as Record<string, number>).handling || 0) + 10;
    additive.reload += 10;
  }

  // Recombinator: user-entered 3 stat bonuses (freeform, no module simulation)
  for (const slot of state.recombinator) {
    if (!slot.stat || slot.value === 0) continue;
    (additive as Record<string, number>)[slot.stat] =
      ((additive as Record<string, number>)[slot.stat] || 0) + slot.value;
  }

  // Group size does NOT affect player damage in Division 2.
  // It only scales enemy HP/armor (used separately in TTK calculations).

  // Target status / marker — named and exotic weapon specific bonuses
  if (weapon && (weapon.kind === 'named' || weapon.kind === 'exotic')) {
    // Pyromaniac AR (burning target): +30% WD, +10% CHC
    if (state.targetStatus === 'burning' && weapon.id === 'pyromaniac') {
      additive.wd += 30;
      additive.chc += 10;
    }
    // Scorpio: +30% WD vs bleeding/burning
    if ((state.targetStatus === 'bleeding' || state.targetStatus === 'burning') && weapon.id === 'scorpio') {
      additive.wd += 30;
    }
    // Razorback: +20% WD + +10% CHC vs any status
    if (state.targetStatus !== 'none' && weapon.id === 'razorback') {
      additive.wd += 20;
      additive.chc += 10;
    }
    // Diamondback: +20% CHD on marked target
    if (weapon.id === 'diamondback' && state.targetPulsed) {
      additive.chd += 20;
    }
    // Regulus: +100% CHD on HS kill marker (use targetPulsed as proxy for marker)
    if (weapon.id === 'regulus' && state.targetPulsed) {
      additive.chd += 100;
    }
  }

  // Generic: any status active gives +10% WD baseline (conservative approx)
  if (state.targetStatus !== 'none' && weapon?.kind !== 'named') {
    additive.wd += 10;
  }

  // Pulsed target: Spotter talent condition
  if (state.targetPulsed && state.backpackTalentId === 'gt_spotter' && state.backpackTalentActive) {
    // Already handled by Spotter amplified — this is a sanity flag
  }

  // Full armor: Vigilance condition
  if (state.fullArmor && state.backpackTalentId === 'gt_vigilance' && state.backpackTalentActive) {
    // Already handled — flag for UI feedback
  }

  // 4pc Set stacks: AMPLIFIED bucket (per v1 / mannequin measurements)
  // NOT additive with WD. Added as a separate multiplier.
  // Auto-detect chest talent: if the chest slot itself carries the set (not named/brand) → set's
  // chest talent is available → cap raised to maxChest (200 for Striker, etc.).
  const chestSlotSetId = state.gear.chest.setId;
  for (const [setId, count] of Object.entries(setCounts)) {
    if (count < 4) continue;
    const meta = findSet4pc(setId);
    if (!meta) continue;
    // Conditional gates — bonus applies only when the corresponding battlefield
    // condition is active (Heartbreaker → pulsed; Negotiator's Dilemma → marked).
    if (meta.requiresPulsed && !state.targetPulsed) continue;
    if (meta.requiresMarked && !state.targetMarked) continue;
    // Set talents auto-activate ONLY if the set piece itself is equipped in that slot.
    // Strikers chest talent (200 cap) works only when chest IS a Strikers piece.
    // Strikers backpack talent (Risk Management, 0.9%/stack) only when backpack IS Strikers.
    const hasChestT = chestSlotSetId === setId;
    const hasBpT = state.gear.backpack.setId === setId;
    const maxStacks = hasChestT && meta.maxChest ? meta.maxChest : meta.maxBase;
    // Auto-max: calculator always shows PEAK DPS with full stacks.
    const effectiveStacks = maxStacks;
    const perStackList = hasBpT && meta.perStackBp ? meta.perStackBp : meta.perStack;
    for (const { stat, value } of perStackList) {
      const totalPct = effectiveStacks * value;
      if (stat === 'wd') {
        // WD bucket: amplified (own multiplier) vs additive based on set data.
        // TU22.1: Striker is ADDITIVE in WD pool (inherited from Striker's Gamble).
        // Hunter's Fury, Negotiator's Dilemma, Future Initiative — AMPLIFIED (per research).
        if (meta.wdAmplified) {
          amplifiedMultipliers.push(totalPct);
        } else {
          additive.wd += totalPct;
        }
      } else if (stat === 'chc') {
        additive.chc += totalPct;
      } else if (stat === 'chd') {
        // CHD stacks go into CHD total (not amplified — it's a crit stat)
        additive.chd += totalPct;
      } else if (stat === 'hsd') {
        additive.hsd += totalPct;
      } else if (stat === 'rof') {
        additive.rof += totalPct;
      }
    }
  }

  // Weapon mods (new schema: { stat, value }).
  // Special cases (stat=null): rawStat "Rounds" / "rounds" → flat magazine bonus (mag_flat).
  //                            rawStat "Rate of Fire" → rof%.
  for (const slot of MOD_SLOTS) {
    const modId = state.weaponMods[slot];
    if (!modId) continue;
    const mod = findMod(modId);
    if (!mod || !mod.value) continue;
    if (mod.stat) {
      (additive as Record<string, number>)[mod.stat] =
        ((additive as Record<string, number>)[mod.stat] || 0) + mod.value;
    } else if (mod.rawStat) {
      const raw = (mod as unknown as { rawStat: string }).rawStat.toLowerCase();
      if (raw === 'rounds') {
        (additive as Record<string, number>).mag_flat =
          ((additive as Record<string, number>).mag_flat || 0) + mod.value;
      } else if (raw === 'rate of fire') {
        additive.rof += mod.value;
      }
    }
  }

  // Specialization — unique passives (HSD, skill power, etc.)
  // ВАЖНО: spec-бонусы применяются к DPS, но НЕ показываются в стат-панели
  // (как в реальной игре — спец-перки скрыты в Weapon Stats panel).
  // Для этого трекаем их в specPassive и в конце вычитаем из summary.additive.
  let reloadEvery3rd50 = false;
  const specPassive: Record<string, number> = {};
  if (state.specializationId) {
    const spec = findSpec(state.specializationId);
    if (spec) {
      for (const { stat, value } of spec.uniqueBonus) {
        (additive as Record<string, number>)[stat] =
          ((additive as Record<string, number>)[stat] || 0) + value;
        specPassive[stat] = (specPassive[stat] || 0) + value;
      }
      // Tree perks — applied only when user toggled them active
      if (spec.treePerks) {
        for (const perk of spec.treePerks) {
          if (!state.activeSpecPerks.includes(perk.id)) continue;
          if (perk.stat === 'reload_every_3rd_50pct') {
            reloadEvery3rd50 = true;
          } else {
            (additive as Record<string, number>)[perk.stat] =
              ((additive as Record<string, number>)[perk.stat] || 0) + perk.value;
            specPassive[perk.stat] = (specPassive[perk.stat] || 0) + perk.value;
          }
        }
      }
    }
  }
  // Spec skill-tree: +15% WD per UNIQUE weapon class (up to 3 different classes)
  // Cap по классу: только 1× +15 на каждый class (не stacks при дубликатах в picks).
  if (state.specClassPicks.length > 0 && weapon) {
    const uniqueClasses = new Set(state.specClassPicks);
    for (const cls of uniqueClasses) {
      const key = `wd_${cls}`;
      (additive as Record<string, number>)[key] =
        ((additive as Record<string, number>)[key] || 0) + 15;
    }
  }

  // PRO-mode manual overrides — REPLACE computed additive value with user-supplied final.
  // Applied AFTER all gear/talent/spec/scp computations, BEFORE deriving weapon-level
  // multipliers (rofMul/magMul/reloadMul) and weaponTypeDamagePct.
  const overrides = state.manualOverrides ?? {};
  for (const [stat, val] of Object.entries(overrides)) {
    if (ADDITIVE_OVERRIDE_KEYS.has(stat)) {
      (additive as Record<string, number>)[stat] = val;
      // If user takes ownership of this stat, remove the spec-passive subtraction
      // that would otherwise hide their value from the displayed panel.
      delete specPassive[stat];
    }
  }

  // Map weapon-class-specific WD to weaponTypeDamagePct (stacks additively with wd)
  let weaponTypeDamagePct = 0;
  if (weapon) {
    const catKey = `wd_${weapon.category}` as keyof typeof additive;
    const classWd = (additive as Record<string, number>)[catKey as string];
    if (typeof classWd === 'number') weaponTypeDamagePct += classWd;
  }

  // Apply weapon-stat bonuses: RoF → RPM, magazine (flat+%), reload %.
  // Handling (эргономика) adds 1:1 to reload speed (game formula).
  // Base reload = empty-mag reload (game UI uses this, not tactical).
  // Example: St. Elmo empty=2.4s with Watch reload +10 + handling 35% (Watch 10 + Strikers 2pc 15 + grip 10):
  //   bonus = 45% → 2.4/1.45 = 1.655s ≈ 1.66s in game ✓
  const rofMul = 1 + (additive.rof || 0) / 100;
  const magFlat = (additive as Record<string, number>).mag_flat || 0;
  const magMul = 1 + (additive.mag || 0) / 100;
  const handlingVal = (additive as Record<string, number>).handling || 0;
  const reloadPct = (additive.reload || 0) + handlingVal;
  let reloadMul = 1 / (1 + reloadPct / 100);
  // Spec tree perk: every 3rd reload is 50% faster. Amortize: avg = (2×full + 1×half)/3 = 5/6.
  if (reloadEvery3rd50) reloadMul = reloadMul * (5 / 6);

  // Weapon-level manual overrides REPLACE the final post-bonus value.
  // Special case: talType=no_reload (Bullet King) — auto-refill mag, sustained DPS = burst.
  const noReloadExotic = weapon?.talType === 'no_reload';
  const wpnBaseDamage = overrides.baseDamage ?? weapon?.baseDamage ?? 0;
  const wpnRpm = overrides.rpm ?? (weapon ? weapon.rpm * rofMul : 0);
  const wpnMagazine = overrides.magazine ?? (weapon ? Math.max(1, Math.round((weapon.magazine + magFlat) * magMul)) : 1);
  const wpnReload = overrides.reloadSeconds ?? (
    weapon ? (noReloadExotic ? 0 : weapon.reloadSeconds * reloadMul) : 1
  );

  const dpsInput: DpsInput = {
    weapon: weapon
      ? {
          baseDamage: wpnBaseDamage,
          rpm: wpnRpm,
          magazine: wpnMagazine,
          reloadSeconds: wpnReload,
          headshotMultiplier: weapon.headshotMultiplier,
        }
      : { baseDamage: 0, rpm: 0, magazine: 1, reloadSeconds: 1, headshotMultiplier: 1.5 },
    additive: {
      // WD for DPS includes talent stacks (Obliterate etc.) at peak.
      // Weapon stat display uses `additive.wd` only (without stacks).
      weaponDamagePct: additive.wd + talentStackWd,
      weaponTypeDamagePct,
      additiveTalentsPct: 0,
    },
    amplified: { multipliers: amplifiedMultipliers },
    crit: {
      chcPct: additive.chc,
      chdPct: additive.chd,
      hsdPct: additive.hsd,
      headshotChancePct: state.headshotChancePct,
    },
    target: {
      damageToArmorPct: additive.dta,
      damageToHealthPct: additive.dth,
      damageOutOfCoverPct: additive.ooc,
      fullArmor: state.fullArmor,
    },
    expertise: { grade: state.expertiseGrade },
  };

  const dps = calculateDps(dpsInput);

  // Headhunter cap: if backpack talent id matches headhunter, cap bulletDamageCritHs
  // Headhunter is a CHEST talent (TU22.1 verified, not backpack as before).
  // Formula: stored = killing_shot_dmg × 1.25 (Perfect/Chainkiller: 1.50)
  //          cap = weapon_base × 8 (×12.5 if HSD > 150%)
  //          next_hit = own_hit + min(stored, cap) — CONSUMED once.
  const hhChestId = state.chestTalentId;
  const isHeadhunter = hhChestId === 'gt_headhunter' || hhChestId === 'gt_perfect_headhunter';
  if (isHeadhunter && weapon) {
    dps.bulletDamageCritHs = applyHeadhunterCap(dps.bulletDamageCritHs, weapon.baseDamage, additive.hsd);
  }

  // DPS ramp-up simulation with stack accumulation
  const rampTimes = [0, 1, 2, 3, 5, 7, 10];
  const ramp = rampTimes.map((t) => {
    let wdExtra = 0;
    const stacks: Record<string, number> = {};
    // Set 4pc stacks accumulate over time
    for (const [setId, count] of Object.entries(setCounts)) {
      if (count < 4) continue;
      const meta = findSet4pc(setId);
      if (!meta) continue;
      const maxSt = meta.maxBase;
      const actualStacks = stacksAtTime(meta.trigger, maxSt, t);
      stacks[setId] = actualStacks;
      for (const { stat, value } of meta.perStack) {
        if (stat === 'wd') wdExtra += actualStacks * value;
      }
    }
    // Weapon talent stacks
    if (weapon?.talType && ['kill', 'stacks', 'shot_cover', 'hs_kill'].includes(weapon.talType)) {
      const max = weapon.talMax || 1;
      const bonus = weapon.talBonus || 0;
      const actualStacks = stacksAtTime(weapon.talType, max, t);
      stacks['_wpn_tal'] = actualStacks;
      if (max > 0) wdExtra += (actualStacks / max) * bonus;
    }
    // Compute DPS with extra WD
    const rampInput: DpsInput = {
      ...dpsInput,
      additive: {
        ...dpsInput.additive,
        weaponDamagePct: dpsInput.additive.weaponDamagePct + wdExtra,
      },
    };
    return { t, dps: calculateDps(rampInput).burstDps, stacks };
  });

  // Burn DPS (DoT). Applied by Iron Lung (Ardent talent fire bullets), Pyromaniac AR,
  // Firestarter, etc. Independent of weapon damage — uses Status Effects % stat only.
  const appliesBurn = !!(weapon as unknown as { appliesBurn?: boolean })?.appliesBurn;
  let burnSummary: BuildSummary['burn'];
  if (appliesBurn) {
    const statusEffectsPct = (additive as Record<string, number>).status_effects || 0;
    const burnDurationPct = (additive as Record<string, number>).burn_duration || 0;
    const dpsPerTick = SKILL_CURVE_LVL40 * BURN_BASE_COEFF * (1 + statusEffectsPct / 100);
    const duration = BURN_BASE_DURATION * (1 + burnDurationPct / 100);
    burnSummary = {
      dpsPerTick,
      duration,
      totalPerApplication: dpsPerTick * duration,
      statusEffectsPct,
      burnDurationPct,
    };
  }

  // Bleed DPS (DoT). Applied by Scorpio (mark on HS), Diamondback (mark on reload), Razorback
  // (HS chain), Regulus on-HS-kill, etc. Scales with Status Effects stat (same as Burn).
  const appliesBleed = !!(weapon as unknown as { appliesBleed?: boolean })?.appliesBleed;
  let bleedSummary: BuildSummary['bleed'];
  if (appliesBleed) {
    const statusEffectsPct = (additive as Record<string, number>).status_effects || 0;
    const bleedDurationPct = (additive as Record<string, number>).bleed_duration || 0;
    const dpsPerTick = SKILL_CURVE_LVL40 * BLEED_BASE_COEFF * (1 + statusEffectsPct / 100);
    const duration = BLEED_BASE_DURATION * (1 + bleedDurationPct / 100);
    bleedSummary = {
      dpsPerTick,
      duration,
      totalPerApplication: dpsPerTick * duration,
      statusEffectsPct,
      bleedDurationPct,
    };
  }

  // Poison DPS (DoT). Applied by Scorpio (poison shotgun), Pestilence (Plague stacks),
  // Chem Oxidizer, Bighorn shield-break, etc. Raw note «Не скалируется модбонусом» —
  // does NOT scale with Status Effects stat. Duration can scale with poison_duration.
  const appliesPoison = !!(weapon as unknown as { appliesPoison?: boolean })?.appliesPoison;
  let poisonSummary: BuildSummary['poison'];
  if (appliesPoison) {
    const poisonDurationPct = (additive as Record<string, number>).poison_duration || 0;
    const dpsPerTick = SKILL_CURVE_LVL40 * POISON_BASE_COEFF; // no status_effects scaling
    const duration = POISON_BASE_DURATION * (1 + poisonDurationPct / 100);
    poisonSummary = {
      dpsPerTick,
      duration,
      totalPerApplication: dpsPerTick * duration,
      poisonDurationPct,
    };
  }

  // Subtract spec passive bonuses from DISPLAYED additive (DPS calc already used full).
  // Спец-бонусы остаются в формуле через dpsInput, но скрыты из стат-панели игрока.
  const displayedAdditive = { ...additive } as Record<string, number>;
  for (const [stat, val] of Object.entries(specPassive)) {
    if (stat in displayedAdditive) {
      displayedAdditive[stat] = (displayedAdditive[stat] || 0) - val;
    }
  }

  return {
    dps,
    dpsInput,
    additive: displayedAdditive as BuildSummary['additive'],
    setCounts,
    brandCounts,
    ramp,
    burn: burnSummary,
    bleed: bleedSummary,
    poison: poisonSummary,
  };
}
